import mwo, {
    GetWebSocketResponses,
    WebSocketFactory,
    WebSocketLike,
    WebSocketOptions
} from 'rxjs-websockets';
import { Subject, Observable, share, switchMap } from 'rxjs';

import logger from 'loglevel';

import { shareConfigPublishReplay } from './share-config-publish-replay.js';

export interface WebsocketConnectResult<M> {
    messages: Observable<M>;
    connectionStatus: Observable<number>;
}

type WebSocketStatus = 0 | 1 | 2 | 3;

interface WebSocketEvent {
    type: string;
    code: number;
    message: string;
    wasClean?: boolean;
}

// satisfy the type system...
declare const WebSocket: new (url: string, protocols: string | string[]) => WebSocketLike;

export function websocketConnect<M extends string | Blob>(
    url: string,
    output: Observable<string>,
    protocols: string | string[] | undefined = undefined,
    factory?: WebSocketFactory
): WebsocketConnectResult<M> {
    type WebSocketPayload = string | ArrayBuffer | Blob;
    type MakeWebSocketObservable<T extends WebSocketPayload> = (
        url: string,
        { protocols, makeWebSocket }?: WebSocketOptions
    ) => Observable<GetWebSocketResponses<T>>;
    // Broken by NodeNext module file format
    // https://github.com/insidewhy/rxjs-websockets/issues/206
    const makeWebsocketObservable: MakeWebSocketObservable<M> =
        typeof mwo === 'function' ? mwo : (mwo as { default: MakeWebSocketObservable<M> }).default;
    const status = new Subject<number>();
    const socket = makeWebsocketObservable(url, {
        protocols: protocols || [],
        makeWebSocket: (url, protocols) => {
            if (!factory && typeof WebSocket !== 'function') {
                throw new Error(
                    `Must specify a factory function because WebSocket is ${typeof WebSocket}`
                );
            }
            const ws = factory
                ? factory(url, protocols || [])
                : (new WebSocket(url, protocols) as WebSocketLike);
            if ('addEventListener' in ws) {
                for (const eventName of ['readystatechange', 'open', 'close', 'error']) {
                    (ws as any).addEventListener(eventName, (event: WebSocketEvent) => {
                        if (event.type === 'error') {
                            logger.error(event);
                        } else if (event.type === 'close') {
                            logger.trace(
                                `websocket closed: ${url} ${event.code} ${
                                    event.wasClean ? '' : ' (unclean) '
                                } ${event.message || ''}`
                            );
                        }
                        status.next((ws as any).readyState);
                    });
                }
            }
            if ('readyState' in ws) {
                status.next((ws as any).readyState as WebSocketStatus);
            }
            // makeWebsocketObservable returns the message instead of the code when something goes wrong..
            // Chrome avoids sending messages to the client for 'security reasons' ?
            // We need to hack the 'onclose' so that it will always have a message, otherwise we don't know what
            // went wrong when we get no message.
            type CloseEvent = { code: number; reason: string };
            function handleClose(this: any, event: CloseEvent) {
                if (event.code !== 1000 && event.reason === '') {
                    Object.defineProperty(event, 'reason', { value: `Code ${event.code}` });
                }
                if (this.__onclose2) {
                    this.__onclose2(event);
                }
            }
            ws.onclose = handleClose;
            (ws as any).__onclose = handleClose;
            Object.defineProperty(ws, 'onclose', {
                get: function () {
                    return this.__onclose;
                },
                set: function (value) {
                    this.__onclose2 = value;
                }
            });
            return ws;
        }
    });
    return {
        messages: socket.pipe(
            switchMap(getResponses => {
                return getResponses(output);
            }),
            share()
        ),
        // this one we want to keep even if ref count drops to 0
        connectionStatus: status.pipe(share(shareConfigPublishReplay()))
    };
}
